import { useCallback, useMemo } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import {
  GridColDef,
  GridEditInputCell,
  GridRenderCellParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { ServerDataGrid } from "components/ui/ServerDataGrid";
import dayjs from "dayjs";
import {
  Strategy,
  StrategyGroup,
  useUpdateStrategyMutation,
} from "features/trading/accounts";
import {
  setCurrentStrategy,
  setCurrentStrategyGroup,
} from "features/trading/tradingSlice";
import { useNavigate } from "react-router-dom";
import { asCurrency } from "components/ui/formatters";
import { useAppDispatch } from "features/store";
import { formatFactsetNumberValue, getDaysToEarningsDate, getDaysToExDividend, getHomepageDateColumnHighlightColor, mapOptionNameFromParams } from "helpers";

interface Props {
  group: StrategyGroup;
  isLoading: boolean;
  isFetching: boolean;
  rows: Strategy[];
}

/**
 * The text the Current Call cell shows. That cell is derived from the row
 * rather than read from a field, so filtering it needs the same text as a
 * value -- a column whose value is undefined offers filter operators that can
 * never match, which is indistinguishable from a broken filter.
 */
const currentCallText = ({ current_call, optionSymbolParams }: any) => {
  if (!current_call) {
    return "Open";
  }

  if (!optionSymbolParams?.isCorrect) {
    return "Incorrect Option Symbol";
  }

  return mapOptionNameFromParams(optionSymbolParams);
};

export const StrategyGroupTable = ({ group, isLoading, isFetching, rows }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [updateStrategy] = useUpdateStrategyMutation();

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Symbol",
      renderCell: ({ value, row }: GridRenderCellParams) => {
        return (
          <Box
            component="span"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              const { strategy_group } = row;
              dispatch(setCurrentStrategyGroup(strategy_group));
              dispatch(setCurrentStrategy(row));
              navigate("/stock");
            }}
          >
            {value}
          </Box>
        );
      },
    }, // DB
    {
      field: "current_call_text",
      width: 220,
      headerName: `Current ${rows[0]?.call_or_put ? 'Put' : 'Call'}`,
      valueGetter: ({ row }: GridValueGetterParams) => currentCallText(row),
      renderCell: ({ row: { current_call, optionSymbolParams } }: GridRenderCellParams) => {
        const isOpen = Boolean(!current_call);
        if (isOpen) {
          return <Box component="span" sx={{ color: "green" }}>Open</Box>;
        }

        if (!optionSymbolParams.isCorrect) {
          return <Box component="span" sx={{ color: "red" }}>Incorrect Option Symbol</Box>;
        }

        const { expirationDate } = optionSymbolParams;

        const daysDiff = expirationDate.diff(dayjs(), "day");
        const styles = daysDiff === 0 ? { backgroundColor: "#7FFF00" } : daysDiff < 7 ? { backgroundColor: "yellow" } : null;
        return (
          <Box component="span" sx={styles}>
            {mapOptionNameFromParams(optionSymbolParams)}
          </Box>
        );
      },
    }, // DB
    {
      field: "aum",
      headerName: "AUM",
      width: 150,
      valueFormatter: ({ value }: GridValueFormatterParams) =>
        asCurrency(value),
    }, // Calculated
    {
      field: "last_price",
      headerName: "Last Price",
      renderCell: ({ value, row: { current_call, optionSymbolParams } }: GridRenderCellParams) => {
        const needCheckITM = current_call && optionSymbolParams.isCorrect && value && value !== "N/A";
        if (needCheckITM && optionSymbolParams.strikePrice < parseFloat(value)) {
          return <Box component="span" sx={{ backgroundColor: "#7FFF00", fontWeight: "bold", width: "100%", textAlign: "center" }}>
            {value}
          </Box>
        }

        return value;
      },
    },
    { field: "option_price", width: 120, headerName: "Option Price" }, // FC
    {
      field: "cost_basis",
      headerName: "Basis",
      type: "number",
      editable: true,
      renderEditCell: (params: any) => (
        <GridEditInputCell
          {...params}
          inputProps={{
            min: 0.01,
            step: 0.01,
          }}
          onKeyPress={(event: any) => {
            if (event.code === "Minus") {
              event.preventDefault();
            }
          }}
        />
      ),
    },
    {
      field: "gain_n_loss_per_contract",
      headerName: "G/L",
      renderCell: ({ value }: GridRenderCellParams) => (
        <Box
          component="span"
          style={{ color: value > 0 ? "green" : value === 0 ? "black" : "red" }}
        >
          {value}
        </Box>
      ),
    }, // Calculated
    {
      field: "totalGL",
      headerName: "Total G/L",
      width: 150,
      renderCell: ({ value }: GridRenderCellParams) => {
        const fontColor = value === "N/A" || value === 0 ? "inherit" : value > 0 ? "green" : "red";
        const formattedValue = value !== "N/A" ? asCurrency(value) : value;
        return <Box component="span" sx={{ color: fontColor }}>{formattedValue}</Box>
      },
    }, // Calculated
    { field: "delta_mid",
      headerName: "Delta Mid",
      valueFormatter: ({ value }: GridValueFormatterParams) => formatFactsetNumberValue(value),
    },
    {
      field: "position_delta",
      width: 120,
      headerName: "Position Delta",
      valueFormatter: ({ value }: GridValueFormatterParams) => formatFactsetNumberValue(value),
    },
    {
      field: "dividend_ex_date",
      width: 120,
      headerName: "DVD EX DT",
      renderCell: ({ row, value }: GridRenderCellParams) => {
        if (!value) {
          return (<Box component="span">N/A</Box>);
        }

        const daysTo = getDaysToExDividend(row)
        const backgroundColor = getHomepageDateColumnHighlightColor(daysTo);

        return (
          <Box component="span" sx={{ backgroundColor, width: "100%", textAlign: "center" }}>
            {value}
          </Box>
        );
      },
    }, // FC
    {
      field: "dividend_amt",
      headerName: "DIV AMT",
      valueFormatter: ({ value }: GridValueFormatterParams) => formatFactsetNumberValue(value),
    }, // FC
    {
      field: "days_to_ex_dividend",
      width: 120,
      headerName: "Days to Ex Div",
      valueFormatter: ({ value }: GridValueFormatterParams) => value && value >= 0 ? value : "N/A",
    }, // Calculated
    {
      field: "next_earnings_date",
      width: 120,
      headerName: "Earnings DT",
      renderCell: ({ row, value }: GridRenderCellParams) => {
        if (!value) {
          return (<Box component="span">N/A</Box>);
        }

        const daysTo = getDaysToEarningsDate(row)
        const backgroundColor = getHomepageDateColumnHighlightColor(daysTo);

        return (
          <Box component="span" sx={{ backgroundColor, width: "100%", textAlign: "center" }}>
            {value}
          </Box>
        );
      },
    },
  ].map((col) => ({
    ...col,
    align: "center",
    headerAlign: "center",
    sortable: false,
  }));

  // Both totals are the group's, deliberately: they read from the rows prop
  // rather than the grid's filtered rows, so narrowing the table with a column
  // filter leaves the group's G/L and AUM on screen as a reference figure.
  const totalGL = useMemo(() => {
    return rows.reduce((acc: any, cur: any) => {
      if (cur?.totalGL === "N/A") {
        return acc;
      }

      return acc + parseFloat(cur?.totalGL || 0);
    }, 0);
  }, [rows]);

  const totalAUM = useMemo(() => {
    const total = rows.reduce((acc: any, cur: any) => {
      return acc + parseFloat(cur?.aum! || 0);
    }, 0);

    return asCurrency(total);
  }, [rows]);

  const renderTotals = useCallback(() => {
    const totalGlColor =
      totalGL === 0
      ? "inherit"
      : totalGL > 0
      ? "green"
      : "red";
    return (
      <Grid container>
        <Grid item>
          <Typography variant="h5" component="h5" sx={{ margin: 2 }}>
            Total G/L: <span style={{ color: totalGlColor }}>{asCurrency(totalGL)}</span>
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h5" component="h5" sx={{ margin: 2 }}>
            Total AUM: {totalAUM}
          </Typography>
        </Grid>
      </Grid>
    );
  }, [totalGL, totalAUM, asCurrency]);

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid>
      <Typography variant="h4" component="h4">
        {group.name}
      </Typography>
      <ServerDataGrid
        rows={rows}
        // rowCount={data.count}
        loading={isLoading || isFetching}
        columns={columns}
        // ServerDataGrid defaults to server-side filtering, which needs a
        // filter model wired to a request. This grid has none: every row of
        // the group is already here, enriched with live prices and grouped in
        // the browser, so the column filters silently did nothing. The rows
        // are local, so filter them locally.
        filterMode="client"
        density="compact"
        slots={{
          footer: renderTotals,
        }}
        onCellEditStop={(params: any, event: any) => {
          const {
            field,
            row: { id }
          } = params;
          if (field === "cost_basis") {
            const newValue = parseFloat(event.target.value);
            updateStrategy({
              id,
              current_call_price: newValue ? newValue.toFixed(2) : null,
            });
          }
        }}
      />
    </Grid>
  );
};
